import { call, put, takeLatest, select } from 'redux-saga/effects';
import R from 'ramda';
import JSZip from 'jszip';
import JSZipUtils from 'jszip-utils';
import { saveAs } from 'file-saver';

import AssetLandingSagas from './components/assets-landing/__storeFiles__/sagas';
import secondaryTemplateListSagas from './components/contracting/master-template-list/__storeFiles__/sagas';
import secondaryTemplateSagas from './components/contracting/master-template/__storeFiles__/sagas';
import { workspaceSagas } from './components/workspace/sagas';
import { massMessageSagas } from './components/massmessaging/sagas';
import contractingSagas, { refreshContractDoc } from './components/contracting/__storeFiles__/sagas';
import SupplierUpdatesSaga from './components/contracting/supplier-updates-queue/__storeFiles__/saga';
import sidebarHelpSagas from './components/sidebarHelp/sagas';
import assetsConsolidation from './components/assets-consolidation/__storeFiles__/sagas';
import profileSagas from './components/profile/__storeFiles__/saga';
import contractingDashboard from './components/contracting-dashboard/__storeFiles__/sagas';
import massRateChnageSagas from './stores/contracting/mass-rate-change/sagas';
import partnerDetailsSagas from './components/partner-details/__store__/sagas';
import thirdPartyTracking from './middleware/thirdPartyTracking';
import settingsSagas from './components/settings/__store__/sagas';
import connectionSagas from './components/connections/__storeFiles__/sagas';
import dummyAcctForCRSagas, { refreshPolicyNotes } from './components/contracting/dummy-account/__store-files__/sagas';
import integrationSagas from './stores/integration/integration-sagas';
import billingSagas from './stores/billing/billing-sagas';
import s3Upload from './utils/s3Upload';
import { fileUrls, callApi, account } from './api';

function* refreshDataInPage() {
  if (window.location.href.indexOf('assets-landing-signing') > -1) {
    const { crId } = yield select(state => state.assetsLandingReducer);
    if (crId) {
      yield refreshPolicyNotes({ args: [{ crId }] });
    }
  }
  if (
    window.location.pathname.indexOf('/contracting/contract/') === 0
    || window.location.pathname.indexOf('/contracting/draft/') === 0
    || window.location.pathname.indexOf('/contracting/workspace/') === 0
  ) {
    yield refreshContractDoc({ bgRefresh: true });
  }
}

function* handleDummyTemplateErrors({ error }) {
  if (error.message === 'Not Authorized to This Doc') {
    yield put({
      type: 'GET_CONTRACT_INFO_ERROR',
      payload: {
        errorOccurs: window.location.href.indexOf(process.env.SECONDARY_SYSTEM_URL) > -1 ? 'In order to protect the security and integrity of rate data, you can only have one rate sheet open at a time.' : `Something is wrong, please close the tab and reopen.`,
      },
    });
  }
}

function* updateUIpreferences({ type, payload }) {
  yield apiSaga(type, account.updateUIpreferences, [payload]);
}

/* Sagas from components export */

export default function* rootSaga() {
  yield [
    takeLatest('REFRESH_DATA_IN_PAGE_BACKGROUND', refreshDataInPage),
    takeLatest('{rates-draft} REFRESH_LEFT_NAV_ERROR', handleDummyTemplateErrors),
    takeLatest('updateUIpreferences', updateUIpreferences),
  ]
    .concat(workspaceSagas)
    .concat(AssetLandingSagas)
    .concat(contractingSagas)
    .concat(massMessageSagas)
    .concat(sidebarHelpSagas)
    .concat(assetsConsolidation)
    .concat(profileSagas)
    .concat(contractingDashboard)
    .concat(partnerDetailsSagas)
    .concat(thirdPartyTracking)
    .concat(massRateChnageSagas)
    .concat(secondaryTemplateListSagas)
    .concat(secondaryTemplateSagas)
    .concat(settingsSagas)
    .concat(SupplierUpdatesSaga)
    .concat(connectionSagas)
    .concat(dummyAcctForCRSagas)
    .concat(integrationSagas)
    .concat(billingSagas)
    .concat([]);
}

/* Saga Utils */

// checks if a value is an uploadable File class
export function* checkUpload(val, type) {
  // upload the File to s3
  yield put({ type: `${type}_S3UPLOAD_STARTED` });
  const retval = yield s3Upload.s3uploadPromise(val);
  yield put({ type: `${type}_S3UPLOAD_SUCCESS`, payload: retval });
  return retval;
}

// generic API call wrapper
export function* apiSaga(type, fn, args) {
  yield put({ type: `${type}_STARTED`, args });

  // look for files and upload them
  const nuArgs = [];
  for (const arg of args) {
    if (typeof arg === 'object' && arg !== null && !Array.isArray(arg)) {
      const item = {};
      for (const key in arg) {
        if (Array.isArray(arg[key])) {
          item[key] = [];
          for (const val of arg[key]) {
            if (val instanceof File) {
              item[key].push(yield checkUpload(val, type));
            } else {
              item[key].push(val);
            }
          }
        } else {
          if (arg[key] instanceof File) {
            item[key] = yield checkUpload(arg[key], type);
          } else {
            item[key] = arg[key];
          }
        }
      }
      nuArgs.push(item);
    } else if (Array.isArray(arg)) {
      const item = [];
      for (const val of arg) {
        if (val instanceof File) {
          item.push(yield checkUpload(val, type));
        } else {
          item.push(val);
        }
      }
      nuArgs.push(item);
    } else {
      nuArgs.push(arg);
    }
  }
  // do the actual API Call
  const { response, error } = yield call(fn, ...nuArgs);
  if (error) {
    if (R.pathOr(0, ['response', 'status'], error) === 403) {
      yield put({
        type: 'GLOBAL_ERROR_TRIGGERED',
        payload: { noAccessDialog: true }
      });
    }
    yield put({ type: `${type}_ERROR`, payload: error.message, error, args });
    const url = R.pathOr('<??>', ['request', 'url'], error);
    const errorStr = (() => {
      const method = R.pathOr('access', ['request', 'method'], error);
      return `[API] unable to ${method} ${url}`
    })();
    if (window.Rollbar) {
      window.Rollbar.warning(errorStr, {
        error,
        args: R.omit(['headers'], args),
      });
    } else {
      console.warn(errorStr);
    }
  } else {
    yield put({ type: `${type}_SUCCESS`, payload: response, args });
  }
}

function* getBinaryContent(url) {
  return new Promise((resolve, reject) => {
    return JSZipUtils.getBinaryContent(url, (err, data) => {
      if (err) return resolve(null);
      resolve(data);
    });
  });
}
function* generateZipAsync(zip) {
  return new Promise(resolve => {
    return zip
    .generateAsync({ type: 'blob' })
    .then(resolve)
    .catch(err => {
      console.log('generateZipAsync error', err);
    });
  });
}
function* updateDownloadCountNFinalise(zip, count, totalLength, actionType, zipFileName) {
  if (count === totalLength) {
    yield put({ type: `${actionType || '[contracting-download]BULK_DOWNLOAD'}_SUCCESS` });
    zip
    .generateAsync({ type: 'blob' })
    .then(content => saveAs(content, zipFileName));
  } else {
    yield put({ type: `[contracting-download]UPDATE_PROPS`, payload: { downloadedCount: count + 1 } });
  }
}

export function* zipDownloadContracts({ actionType, contractIds, ...extraPayload }) {
  const zipFileName = (() => {
    const {
      downloadRateFormat,
      zipFileName,
    } = extraPayload;
    if (downloadRateFormat === 'quickview_desummarized') {
      return `${zipFileName || ''}${zipFileName ? '-' : ''}Quickview (xls) Rates.zip`;
    } else if (downloadRateFormat === 'quickview_summarized') {
      return `${zipFileName || ''}${zipFileName ? '-' : ''}Quickview w/summary (xls) Rates.zip`;
    } else if (downloadRateFormat === 'condensed') {
      return `${zipFileName || ''}${zipFileName ? '-' : ''}Compact Version Rates.zip`;
    }
    return `${zipFileName || ''}${zipFileName ? '-' : ''}Full Version Rates.zip`;
  })();
  yield put({ type: `${actionType || '[contracting-download]BULK_DOWNLOAD'}_STARTED` });
  // this is to get the list of eligible contracts based on what user selected
  const { response, error } = yield callApi(`/proxy/contracting/pdf-zip`, 'post', { contractIds });
  if (error) return yield put({ type: `${actionType || '[contracting-download]BULK_DOWNLOAD'}_ERROR` });
  const contracts = response;
  if (!(contracts && contracts.length)) {
    return yield put({ type: `${actionType || '[contracting-download]BULK_DOWNLOAD'}_SUCCESS` });
  }
  yield put({ type: `[contracting-download]UPDATE_PROPS`, payload: { validLengthForBulkDownload: contracts.length } });
  const zip = new JSZip();
  let count = 0;

  for (let contractIndex = 0; contractIndex < contracts.length; contractIndex++) {
    const contract = contracts[contractIndex];
    const { from, to, locationName, files, templateId, respondedContracts } = contract;
    // if external, download files
    if (files && files.length && !templateId) {
      const externalZip = new JSZip();
      let fileCount = 0;
      for (let fileIndex = 0; fileIndex < files.length; fileIndex++) {
        const file = files[fileIndex];
        const bufferData = yield* getBinaryContent(`/proxy/s3/file?key=${encodeURIComponent(file.path)}`);
        const fileData = yield bufferData; // not sure why need to yield twice, but it works
        if (fileData) externalZip.file(`${count + 1}-${fileCount + 1}-${file.name}`, fileData, { binary: true });
        fileCount++;
        if (fileCount === files.length) {
          const content = yield generateZipAsync(externalZip);
          if (content) zip.file(`${contractIndex + 1}-${to.name}.zip`, content, { binary: true });
          count++;
          yield updateDownloadCountNFinalise(zip, count, contracts.length, actionType, zipFileName);
        }
      }
    } else if (respondedContracts && respondedContracts.length && !templateId) {
      const respondedZip = new JSZip();
      let subContractCount = 0;
      for (let rcIndex = 0; rcIndex < respondedContracts.length; rcIndex++) {
        const respondedContract = respondedContracts[rcIndex];
        const bufferData = yield getBinaryContent(fileUrls.getDownloadTCContractingDoc({
          ...extraPayload,
          docId: respondedContract._id,
        }), yield);
        const contractData = yield bufferData; // not sure why need to yield twice, but it works
        if (contractData) {
          const contractName = `${contractIndex + 1}-${respondedContract.from.name}-${respondedContract.locationName}`;
          respondedZip.file(
            `${contractName}.${(extraPayload.downloadRateFormat || '').split('_')[0] === 'quickview' ? 'xlsx' : 'pdf'}`,
            contractData,
            { binary: true },
          );
        }
        subContractCount++;
        if (subContractCount === respondedContracts.length) {
          const content = yield generateZipAsync(respondedZip);
          if (content) zip.file(`${contractIndex + 1}-${to.name}.zip`, content, { binary: true });
          count++;
          yield updateDownloadCountNFinalise(zip, count, contracts.length, actionType, zipFileName);
        }
      }
    } else {
      // if TC format, download from magneto
      const bufferData = yield getBinaryContent(fileUrls.getDownloadTCContractingDoc({
        ...extraPayload,
        docId: contract._id,
      }), yield);
      const data = yield bufferData;
      if (data) {
        const fileName = `${contractIndex + 1}-${to.name}-${from.name}-${locationName}`;
        zip.file(
          `${fileName}.${(extraPayload.downloadRateFormat || '').split('_')[0] === 'quickview' ? 'xlsx' : 'pdf'}`,
          data,
          { binary: true },
        );
      }
      count++;
      yield updateDownloadCountNFinalise(zip, count, contracts.length, actionType, zipFileName);
    }
  }
}

export function* zipDownloadTemplates({ actionType, templates, downloadRateFormat, templateGroupName, ...extraPayload }) {
  yield put({ type: `${actionType || '[contracting-download]BULK_DOWNLOAD'}_STARTED'` });
  const zip = new JSZip();
  let count = 0;
  for (let templateIndex = 0; templateIndex < templates.length; templateIndex++) {
    const template = templates[templateIndex];
    const { templateName, locationName } = template;
    const data = yield getBinaryContent(fileUrls.getDownloadTCContractingDoc({
      docId: template._id,
      collection: 'template',
      locale: extraPayload.locale,
      ratesOnly: extraPayload.ratesOnly,
      downloadRateFormat,
    }));
    if (data) zip.file(`${count + 1}-${locationName}-${templateName}.pdf`, data, { binary: true });
    count++;
    yield updateDownloadCountNFinalise(zip, count, templates.length, actionType, extraPayload.ratesOnly ? `${templateGroupName} Compact Version Rates.zip` : `${templateGroupName} Full Version Rates.zip`);
  }
}



// WEBPACK FOOTER //
// ./src/app/sagas.js